import React, { useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import DashboardCard from './DashboardCard';
import { Legend, LegendItem, Footnote } from '../styles';
import { CATEGORY_FOLD_COLOR, formatTry } from '../constants';

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 7px 0;
`;

const DeptName = styled.div`
  width: 96px;
  flex-shrink: 0;
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Track = styled.div`
  flex: 1;
  display: flex;
  height: 16px;
  border-radius: 4px;
  overflow: hidden;
  background: #F7F9FB;
`;

const Segment = styled.div`
  width: ${({ $pct }) => $pct}%;
  background: ${({ $color }) => $color};
  border-right: 2px solid #fff;

  &:last-child { border-right: none; }
`;

const Amount = styled.div`
  width: 92px;
  flex-shrink: 0;
  text-align: right;
  font-size: 12px;
  font-weight: 700;
  color: #52514e;
`;

export default function DeptCategoryCard({ slice, categoryColors }) {
  const { t } = useTranslation();
  const data = slice.data;

  const model = useMemo(() => {
    const rows = data?.rows || [];
    if (rows.length === 0) return null;

    const byDept = new Map();
    rows.forEach((r) => {
      const key = r.departmentId || 'none';
      if (!byDept.has(key)) byDept.set(key, { name: r.department, total: 0, segments: [] });
      const d = byDept.get(key);
      d.total += r.total;
      d.segments.push(r);
    });

    const depts = [...byDept.values()]
      .sort((a, b) => b.total - a.total)
      .map((d) => ({
        ...d,
        segments: d.segments.sort((a, b) => b.total - a.total),
      }));

    const maxTotal = Math.max(1, ...depts.map((d) => d.total));
    const legend = new Map();
    rows.forEach((r) => legend.set(r.categoryId, { name: r.categoryName, color: categoryColors.get(r.categoryId) || CATEGORY_FOLD_COLOR }));

    return { depts, maxTotal, legend: [...legend.values()] };
  }, [data, categoryColors]);

  return (
    <DashboardCard
      cardKey="departman-kategori"
      title={t('expenseDashboard.deptCategory.title')}
      meta={t('expenseDashboard.deptCategory.meta')}
      span={4}
      spanMd={6}
      loading={slice.loading}
      empty={!model}
      emptyText={t('expenseDashboard.deptCategory.empty')}
    >
      {model && (
        <div>
          {model.depts.map((d) => (
            <Row key={d.name || 'none'}>
              <DeptName title={d.name || t('expenseDashboard.deptCategory.unassigned')}>
                {d.name || t('expenseDashboard.deptCategory.unassigned')}
              </DeptName>
              <Track style={{ width: `${Math.round((d.total / model.maxTotal) * 100)}%`, flex: 'none' }}>
                {d.segments.map((s) => (
                  <Segment
                    key={s.categoryId}
                    $pct={Math.round((s.total / d.total) * 100)}
                    $color={categoryColors.get(s.categoryId) || CATEGORY_FOLD_COLOR}
                    title={`${s.categoryName}: ${formatTry(s.total)}`}
                  />
                ))}
              </Track>
              <Amount>{formatTry(d.total)}</Amount>
            </Row>
          ))}
          <Legend>
            {model.legend.map((entry) => (
              <LegendItem key={entry.name} $color={entry.color}>{entry.name}</LegendItem>
            ))}
          </Legend>
          {data?.nonTryCount > 0 && <Footnote>{t('expenseDashboard.fxExcluded', { count: data.nonTryCount })}</Footnote>}
        </div>
      )}
    </DashboardCard>
  );
}
